// Conta do usuário: validação de senha nova, exportação dos dados (LGPD) e exclusão da conta.
// Extraído de App.js sem alteração de comportamento.
import { sb } from "./supabase";
import { LEGAL, PROFILE_COLS } from "./constants";

// ── Senhas: mínimo de 8 caracteres e bloqueio de senhas vazadas ────────────────
// Consulta a base pública Have I Been Pwned por "k-anonimato": só os 5 primeiros
// caracteres do hash SHA-1 saem do aparelho; a senha nunca é enviada.
// (O equivalente do Supabase só existe no plano Pro.)
export async function checkNewPassword(pw) {
  if (!pw || pw.length < 8) return "A senha deve ter pelo menos 8 caracteres.";
  try {
    if (!window.crypto?.subtle) return null;
    const buf = await window.crypto.subtle.digest("SHA-1", new window.TextEncoder().encode(pw));
    const hex = Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, "0")).join("").toUpperCase();
    const ctrl = new AbortController(); const timer = setTimeout(() => ctrl.abort(), 5000);
    const res = await fetch("https://api.pwnedpasswords.com/range/" + hex.slice(0, 5), { headers: { "Add-Padding": "true" }, signal: ctrl.signal });
    clearTimeout(timer);
    if (!res.ok) return null;
    const suffix = hex.slice(5);
    const leaked = (await res.text()).split("\n").some(line => { const [h, c] = line.trim().split(":"); return h === suffix && parseInt(c, 10) > 0; });
    if (leaked) return "Essa senha já apareceu em vazamentos de dados na internet. Escolha outra senha.";
  } catch {}
  return null; // se a consulta falhar, não impede o cadastro
}

// Apaga as imagens que o usuário enviou (pastas posts/<uid>/ e resumos/<uid>/) — usado ao excluir a conta
export async function deleteUserFiles(uid) {
  if (!uid) return;
  for (const bucket of ["posts", "resumos"]) {
    try {
      for (let round = 0; round < 50; round++) {
        const { data, error } = await sb.storage.from(bucket).list(uid, { limit: 100 });
        if (error || !data || !data.length) break;
        const paths = data.filter(f => f.name).map(f => `${uid}/${f.name}`);
        if (!paths.length) break;
        const { error: rmErr } = await sb.storage.from(bucket).remove(paths);
        if (rmErr) break;
      }
    } catch (e) { console.warn("deleteUserFiles:", bucket, e); }
  }
}

// Reúne todos os dados do usuário (usado pelo relatório legível e pelo JSON técnico)
async function collectMyData(user) {
  const uid = user?.id;
  if (!uid) throw new Error("Usuário não encontrado.");
  const rows = async (tbl, col = "user_id", cols = "*") => {
    try { const { data, error } = await sb.from(tbl).select(cols).eq(col, uid); return error ? { erro: error.message } : (data || []); }
    catch (e) { return { erro: String(e) }; }
  };
  let imagens = [];
  try {
    const { data } = await sb.storage.from("posts").list(uid, { limit: 1000 });
    imagens = (data || []).filter(f => f.name).map(f => sb.storage.from("posts").getPublicUrl(`${uid}/${f.name}`).data.publicUrl);
  } catch {}
  let imagensResumos = [];   // bucket privado: links válidos por 7 dias
  try {
    const { data } = await sb.storage.from("resumos").list(uid, { limit: 1000 });
    const paths = (data || []).filter(f => f.name).map(f => `${uid}/${f.name}`);
    if (paths.length) {
      const { data: urls } = await sb.storage.from("resumos").createSignedUrls(paths, 7 * 24 * 3600);
      imagensResumos = (urls || []).map(u => u.signedUrl).filter(Boolean);
    }
  } catch {}
  const local = {};
  try { for (let k = 0; k < localStorage.length; k++) { const key = localStorage.key(k); if (key && key.startsWith("sf_")) local[key] = localStorage.getItem(key); } } catch {}
  return {
    sobre: `Cópia dos seus dados pessoais no G17 (${LEGAL.site}), conforme o art. 18 da LGPD.`,
    gerado_em: new Date().toISOString(),
    conta: { id: uid, email: user.email, criada_em: user.created_at, email_confirmado_em: user.email_confirmed_at, ultimo_login: user.last_sign_in_at },
    perfil: await rows("profiles", "id", PROFILE_COLS),
    tempo_total_cronometrado: await rows("timer_seconds"),
    sessoes_de_estudo: await rows("study_sessions"),
    rotinas: await rows("routines"),
    ciclos: await rows("cycles"),
    simulados: await rows("simulados"),
    concursos_personalizados: await rows("user_contests"),
    projecoes: await rows("projections"),
    questoes: await rows("questoes"),
    calendario: await rows("study_calendar"),
    edital_verticalizado: await rows("edital_progress"),
    cadernos_de_resumos: await rows("notebooks"),
    resumos: await rows("notes"),
    baralhos_de_flashcards: await rows("flash_decks"),
    flashcards: await rows("flashcards"),
    taf: await rows("taf"),
    uso_da_plataforma_tempo_por_tela: await rows("activity_time"),
    uso_da_plataforma_telas_abertas: await rows("activity_events"),
    publicacoes: await rows("posts"),
    comentarios: await rows("comments"),
    curtidas: await rows("post_likes"),
    avisos_recebidos: await rows("notifications", "target_user_id"),
    estado_da_comunidade: await rows("community_state"),
    imagens_enviadas: imagens,
    imagens_dos_resumos: imagensResumos,
    armazenamento_local_deste_navegador: local,
  };
}

function downloadFile(content, type, filename) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

// Formato técnico (portabilidade, art. 18, V): JSON com tudo
export async function exportMyDataJson(user) {
  const out = await collectMyData(user);
  downloadFile(JSON.stringify(out, null, 2), "application/json", `g17-meus-dados-${new Date().toISOString().slice(0, 10)}.json`);
}

// Relatório legível (acesso, art. 18, II): página HTML organizada, que pode ser impressa ou salva em PDF
export async function exportMyData(user) {
  const d = await collectMyData(user);
  downloadFile(buildDataReport(d), "text/html;charset=utf-8", `g17-meus-dados-${new Date().toISOString().slice(0, 10)}.html`);
}

function buildDataReport(d) {
  const esc = v => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  const arr = v => Array.isArray(v) ? v : [];
  const first = v => arr(v)[0] || null;
  const dt = (iso, withTime = true) => {
    if (!iso) return "—";
    const s = String(iso);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) { const [y, m, dd] = s.split("-"); return `${dd}/${m}/${y}`; }
    const x = new Date(s); if (isNaN(x)) return esc(s);
    return x.toLocaleString("pt-BR", withTime ? { dateStyle: "short", timeStyle: "short" } : { dateStyle: "short" });
  };
  const dur = sec => { sec = Math.max(0, Math.round(Number(sec) || 0)); const h = Math.floor(sec / 3600), m = Math.round((sec % 3600) / 60); return h ? `${h}h ${String(m).padStart(2, "0")}min` : `${m}min`; };
  const durMin = min => dur((Number(min) || 0) * 60);
  const monthName = ym => { const [y, m] = String(ym).split("-"); const n = ["janeiro","fevereiro","março","abril","maio","junho","julho","agosto","setembro","outubro","novembro","dezembro"][Number(m) - 1]; return n ? `${n.charAt(0).toUpperCase() + n.slice(1)} de ${y}` : esc(ym); };
  const DAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
  const table = (head, rowsArr) => rowsArr.length
    ? `<div class="tw"><table><thead><tr>${head.map(h => `<th>${esc(h)}</th>`).join("")}</tr></thead><tbody>${rowsArr.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join("")}</tr>`).join("")}</tbody></table></div>`
    : `<p class="empty">Nenhum registro.</p>`;
  const kv = pairs => `<dl>${pairs.map(([k, v]) => `<dt>${esc(k)}</dt><dd>${v === undefined || v === null || v === "" ? "—" : v}</dd>`).join("")}</dl>`;
  const section = (title, body, note) => `<section><h2>${esc(title)}</h2>${note ? `<p class="note">${note}</p>` : ""}${body}</section>`;
  const details = (summary, body) => `<details><summary>${esc(summary)}</summary>${body}</details>`;

  const p = first(d.perfil) || {};
  const conta = d.conta || {};
  const sessions = arr(d.sessoes_de_estudo).slice().sort((a, b) => String(a.recorded_at).localeCompare(String(b.recorded_at)));
  const totalSec = Number(first(d.tempo_total_cronometrado)?.seconds) || sessions.reduce((a, s) => a + (Number(s.seconds) || 0), 0);
  const routines = arr(first(d.rotinas)?.data);
  const cyclesData = first(d.ciclos)?.data || {};
  const cycles = arr(cyclesData.cycles);
  const activeId = cyclesData.activeCycle?.id;
  const sims = arr(first(d.simulados)?.data).slice().sort((a, b) => String(b.date).localeCompare(String(a.date)));
  const projs = arr(first(d.projecoes)?.data);
  const contests = arr(first(d.concursos_personalizados)?.data);
  const cal = first(d.calendario)?.data || {};
  const posts = arr(d.publicacoes).slice().sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
  const comments = arr(d.comentarios).slice().sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
  const likes = arr(d.curtidas);
  const notifs = arr(d.avisos_recebidos).slice().sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
  const imgs = arr(d.imagens_enviadas);
  const STYLE_NAMES = { classico: "Clássico", tatico: "Tático refinado", foco: "Foco calmo", institucional: "Institucional" };

  // Sessões por mês
  const byMonth = {};
  sessions.forEach(s => { const k = String(s.recorded_at || "").slice(0, 7); if (!k) return; byMonth[k] = byMonth[k] || { n: 0, sec: 0 }; byMonth[k].n++; byMonth[k].sec += Number(s.seconds) || 0; });

  // Calendário: total por matéria e lista por dia
  const calDays = Object.entries(cal.days || {}).sort((a, b) => b[0].localeCompare(a[0]));
  const calBulk = Object.entries(cal.bulk || {}).sort((a, b) => b[0].localeCompare(a[0]));
  const perSubject = {};
  calDays.forEach(([, subj]) => Object.entries(subj || {}).forEach(([s, m]) => { perSubject[s] = (perSubject[s] || 0) + (Number(m) || 0); }));
  calBulk.forEach(([, subj]) => Object.entries(subj || {}).forEach(([s, m]) => { perSubject[s] = (perSubject[s] || 0) + (Number(m) || 0); }));

  const summary = [
    ["Tempo cronometrado", dur(totalSec)], ["Sessões de estudo", sessions.length], ["Simulados", sims.length],
    ["Ciclos", cycles.length], ["Rotinas", routines.length], ["Publicações", posts.length], ["Comentários", comments.length],
  ];

  const body = [
    `<header><div class="brand">G17</div><h1>Seus dados no G17</h1>
      <p>Relatório gerado em ${dt(d.gerado_em)} para <strong>${esc(p.public_name || conta.email)}</strong>, conforme o direito de acesso previsto no art. 18 da LGPD.</p>
      <p class="noprint tip">Para guardar em PDF: use Imprimir (Ctrl+P ou Compartilhar → Imprimir) e escolha "Salvar como PDF".</p></header>`,
    `<div class="cards">${summary.map(([k, v]) => `<div class="card"><span>${esc(k)}</span><strong>${esc(v)}</strong></div>`).join("")}</div>`,

    section("Conta e perfil", kv([
      ["Nome público", esc(p.public_name)], ["E-mail", esc(conta.email)], ["Conta criada em", dt(conta.criada_em)],
      ["E-mail confirmado em", dt(conta.email_confirmado_em)], ["Último login", dt(conta.ultimo_login)], ["Último acesso registrado", dt(p.last_seen_at)],
      ["Concurso almejado", esc(/^[a-z0-9-]+$/.test(String(p.target_contest || "")) ? String(p.target_contest).toUpperCase() : p.target_contest)], ["Biografia", esc(p.bio)], ["Cargo/insígnia na comunidade", esc(p.role)],
      ["Foto de perfil", p.avatar_url ? (String(p.avatar_url).startsWith("http") ? `<a href="${esc(p.avatar_url)}">Ver imagem</a>` : "Enviada (guardada no perfil)") : ""],
      ["Aparência", esc([STYLE_NAMES[String(p.theme_style || "").split(":")[0]] || p.theme_style, p.theme_mode === "light" ? "Modo claro" : p.theme_mode === "dark" ? "Modo escuro" : ""].filter(Boolean).join(" · "))],
      ["Termos e Política aceitos", p.terms_version ? `Versão ${esc(p.terms_version)}, em ${dt(p.terms_accepted_at)}` : "Ainda não aceitos"],
      ["Situação da conta", p.banned ? "Suspensa" : "Ativa"],
      ["Identificador interno", `<code>${esc(conta.id)}</code>`],
    ])),

    section("Tempo de estudo", `
      <p><strong>Total cronometrado:</strong> ${dur(totalSec)} em ${sessions.length} sessões.</p>
      ${table(["Mês", "Sessões", "Tempo"], Object.entries(byMonth).sort((a, b) => b[0].localeCompare(a[0])).map(([k, v]) => [monthName(k), v.n, dur(v.sec)]))}
      ${sessions.length ? details(`Ver todas as ${sessions.length} sessões`, table(["Data e hora", "Duração"], sessions.slice().reverse().map(s => [dt(s.recorded_at), dur(s.seconds)]))) : ""}
    `, "Só o tempo registrado pelo cronômetro do Ciclo entra aqui (é o que conta para o ranking)."),

    section("Rotinas", routines.length ? routines.map(r => `<h3>${esc(r.name || "Rotina")}</h3>${table(["Atividade", "Tipo", "Dias", "Horário"],
      arr(r.slots).slice().sort((a, b) => String(a.start).localeCompare(String(b.start))).map(s => [esc(s.label || "—"), s.isStudy === false ? "Outra atividade" : "Estudo", esc(arr(s.days).slice().sort().map(i => DAYS[i] ?? i).join(", ")), `${esc(s.start)} – ${esc(s.end)}`]))}`).join("") : `<p class="empty">Nenhuma rotina.</p>`),

    section("Ciclos de estudo", cycles.length ? cycles.map(c => {
      const subs = arr(c.subjects);
      const agg = {};
      subs.forEach(s => { const k = s.name || "—"; agg[k] = agg[k] || { weight: s.weight, stars: s.stars, meta: 0, studied: 0, blocks: 0 }; agg[k].meta += Number(s.blockMin) || 0; agg[k].studied += Number(s.studiedSec) || 0; agg[k].blocks++; });
      return `<h3>${esc(c.name || "Ciclo")}${c.id === activeId ? ' <span class="tag">Ativo</span>' : ""}</h3>
        <p class="meta">Concurso: ${esc(String(c.contest || "—").toUpperCase())} · ${subs.length} blocos · duração total ${durMin(c.totalMin)}</p>
        ${table(["Matéria", "Peso", "Domínio", "Blocos", "Meta", "Estudado"], Object.entries(agg).map(([n, a]) => [esc(n), esc(a.weight ?? "—"), "★".repeat(Number(a.stars) || 0) + "☆".repeat(Math.max(0, 5 - (Number(a.stars) || 0))), a.blocks, durMin(a.meta), dur(a.studied)]))}`;
    }).join("") : `<p class="empty">Nenhum ciclo.</p>`),

    section("Simulados", table(["Data", "Simulado", "Concurso", "Forma", "Resultado", "Acertos / erros / brancos", "Duração"],
      sims.map(s => [dt(s.date, false), esc(s.name || "—"), esc(String(s.contest || "—").toUpperCase()), s.method === "cebraspe" ? "CEBRASPE" : "Nota direta",
        esc(s.net ?? s.score ?? "—") + (s.redacao != null ? ` <span class="meta">(redação ${esc(s.redacao)})</span>` : ""),
        s.method === "cebraspe" ? `${esc(s.correct ?? "—")} / ${esc(s.wrong ?? "—")} / ${esc(s.blank ?? "—")}` : "—",
        s.durMin ? durMin(s.durMin) : "—"]))),

    section("Projeções", projs.length ? projs.map(pr => `<h3>${esc(pr.name || "Projeção")}</h3>
      <p class="meta">${pr.subject ? `Matéria: ${esc(pr.subject)} · ` : ""}Nota atual: ${esc(pr.currentScore ?? "—")} · Meta: ${esc(pr.target ?? "—")} · Prazo: ${esc(pr.months ?? "—")} meses</p>
      ${table(["Data prevista", "Prova", "Nota necessária", "Nota obtida", "Feita em"], arr(pr.steps).map(st => [dt(st.date, false), esc(st.actualName || st.exam || "—"), esc(st.need ?? "—"), esc(st.actual ?? "—"), st.done ? dt(st.actualDate, false) : "—"]))}`).join("") : `<p class="empty">Nenhuma projeção.</p>`),

    section("Calendário de estudos", (calDays.length || calBulk.length || Object.keys(cal.notes || {}).length) ? `
      ${table(["Matéria", "Tempo total"], Object.entries(perSubject).sort((a, b) => b[1] - a[1]).map(([s, m]) => [esc(s), durMin(m)]))}
      ${calBulk.length ? details("Totais lançados por mês", table(["Mês", "Matéria", "Tempo"], calBulk.flatMap(([ym, subj]) => Object.entries(subj || {}).map(([s, m]) => [monthName(ym), esc(s), durMin(m)])))) : ""}
      ${Object.keys(cal.notes || {}).length ? details(`Anotações (${Object.values(cal.notes).reduce((a, l) => a + arr(l).length, 0)})`, table(["Dia", "Anotação", "Escrita em"], Object.entries(cal.notes).sort((a, b) => b[0].localeCompare(a[0])).flatMap(([day, list]) => arr(list).map(n => [dt(day, false), esc(n.text).replace(/\n/g, "<br>"), dt(n.at)])))) : ""}
      ${calDays.length ? details(`Ver os ${calDays.length} dias registrados`, table(["Dia", "Matéria", "Tempo"], calDays.flatMap(([day, subj]) => Object.entries(subj || {}).map(([s, m]) => [dt(day, false), esc(s), durMin(m)])))) : ""}
    ` : `<p class="empty">Nenhum registro.</p>`),

    contests.length ? section("Concursos personalizados", table(["Sigla", "Nome"], contests.map(c => [esc(c.label), esc(c.full)]))) : "",

    section("Comunidade", `
      <h3>Publicações (${posts.length})</h3>
      ${posts.length ? posts.map(x => `<div class="post"><div class="meta">${dt(x.created_at)}${x.room && x.room !== "geral" ? ` · ${esc(x.room)}` : ""}</div>${x.title ? `<strong>${esc(x.title)}</strong><br>` : ""}${esc(x.content).replace(/\n/g, "<br>")}${x.image_url ? `<div><a href="${esc(x.image_url)}">Ver imagem</a></div>` : ""}</div>`).join("") : `<p class="empty">Nenhuma publicação.</p>`}
      <h3>Comentários (${comments.length})</h3>
      ${comments.length ? table(["Data", "Comentário"], comments.map(c => [dt(c.created_at), esc(c.content).replace(/\n/g, "<br>")])) : `<p class="empty">Nenhum comentário.</p>`}
      <h3>Curtidas</h3><p>Você curtiu ${likes.length} ${likes.length === 1 ? "publicação" : "publicações"}.</p>
      <h3>Imagens enviadas (${imgs.length})</h3>
      ${imgs.length ? `<ul>${imgs.map((u, i) => `<li><a href="${esc(u)}">Imagem ${i + 1}</a></li>`).join("")}</ul>` : `<p class="empty">Nenhuma imagem.</p>`}
    `, "Publicações, comentários e curtidas são visíveis para os outros usuários logados."),

    section("Avisos recebidos", table(["Data", "Aviso", "Lido"], notifs.map(n => [dt(n.created_at), `<strong>${esc(n.title)}</strong>${n.body ? "<br>" + esc(n.body).replace(/\n/g, "<br>") : ""}`, n.is_read ? "Sim" : "Não"])),
      "Apenas avisos enviados diretamente para você. Avisos gerais para todos não aparecem aqui."),

    section("Dados guardados neste navegador", `<p>O G17 também guarda neste aparelho a sessão de login, suas preferências de aparência e cópias temporárias dos seus dados, para funcionar mais rápido. Eles somem ao sair da conta ou limpar os dados do navegador.</p>
      <p class="meta">${Object.keys(d.armazenamento_local_deste_navegador || {}).length} itens guardados neste navegador no momento.</p>`),

    `<footer><p>Quer corrigir algo? Edite em Perfil, no próprio G17. Para excluir tudo: Perfil → Conta → Excluir conta.</p>
      <p>Dúvidas ou pedidos sobre seus dados: <a href="mailto:${esc(LEGAL.email)}">${esc(LEGAL.email)}</a> · Responsável: ${esc(LEGAL.controller)}.</p>
      <p>Precisa levar seus dados para outro serviço? Em Perfil → Conta há também o formato técnico (JSON).</p></footer>`,
  ].join("\n");

  return `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>Seus dados no G17</title>
<style>
  *{box-sizing:border-box} body{margin:0;background:#f6f6f3;color:#1c1f24;font:15px/1.55 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif}
  main{max-width:880px;margin:0 auto;padding:28px 18px 48px}
  header{margin-bottom:20px} .brand{display:inline-block;background:#1c1f24;color:#fff;font-weight:800;letter-spacing:.08em;border-radius:8px;padding:4px 10px;font-size:13px}
  h1{font-size:28px;margin:12px 0 6px;letter-spacing:-.01em} h2{font-size:19px;margin:0 0 12px} h3{font-size:15px;margin:18px 0 8px}
  .tip{background:#fff7e0;border:1px solid #f1dfa6;border-radius:8px;padding:8px 12px;font-size:13px}
  .cards{display:grid;grid-template-columns:repeat(auto-fit,minmax(130px,1fr));gap:10px;margin:0 0 22px}
  .card{background:#fff;border:1px solid #e3e3de;border-radius:12px;padding:12px 14px;display:flex;flex-direction:column;gap:2px}
  .card span{font-size:12px;color:#6b7078} .card strong{font-size:20px}
  section{background:#fff;border:1px solid #e3e3de;border-radius:14px;padding:18px 18px 14px;margin:0 0 16px;break-inside:avoid-page}
  dl{display:grid;grid-template-columns:minmax(150px,220px) 1fr;gap:6px 14px;margin:0} dt{color:#6b7078} dd{margin:0;word-break:break-word}
  table{width:100%;border-collapse:collapse;font-size:14px;margin:4px 0 8px} th{text-align:left;font-weight:600;color:#6b7078;font-size:12px;text-transform:uppercase;letter-spacing:.04em;border-bottom:1px solid #e3e3de;padding:6px 8px}
  td{border-bottom:1px solid #eeeeea;padding:7px 8px;vertical-align:top} tr:last-child td{border-bottom:none}
  .tw{overflow-x:auto;-webkit-overflow-scrolling:touch;margin:0 -4px;padding:0 4px} .tw table{min-width:100%} th{white-space:nowrap}
  .empty,.meta,.note{color:#6b7078} .note{font-size:13px;margin:-6px 0 10px} .meta{font-size:13px}
  .tag{display:inline-block;background:#e7f6ee;color:#1d7a4a;border-radius:6px;padding:0 6px;font-size:12px;font-weight:600;vertical-align:middle}
  .post{border-left:3px solid #e3e3de;padding:4px 0 4px 12px;margin:0 0 12px}
  details{margin:8px 0} summary{cursor:pointer;color:#2f5fd0;font-weight:600;padding:4px 0}
  code{font-size:12px;background:#f1f1ee;border-radius:4px;padding:1px 4px} a{color:#2f5fd0}
  footer{color:#6b7078;font-size:13px;margin-top:24px}
  @media (max-width:600px){ dl{grid-template-columns:1fr} dt{margin-top:6px} table{font-size:13px} th,td{padding:6px 5px} }
  @media print{ body{background:#fff} .noprint{display:none} section,.card{border-color:#ccc} details{display:block} details>*{display:block} summary{display:none} details[open] summary~*, details summary~*{display:block} }
</style></head><body><main>
${body}
</main>
<script>window.addEventListener("beforeprint",function(){document.querySelectorAll("details").forEach(function(x){x.open=true;});});</script>
</body></html>`;
}

// Exclusão completa da conta (Perfil e tela de recusa dos termos)
export async function deleteMyAccount(uid) {
  if (!uid) return "Usuário não encontrado.";
  await deleteUserFiles(uid);                       // imagens enviadas
  const { error } = await sb.rpc("delete_user_account"); // o banco apaga o resto em cascata
  if (error) { console.error("Erro ao excluir conta:", error); return "Não foi possível excluir a conta agora. Tente de novo ou escreva para " + LEGAL.email + "."; }
  try { Object.keys(localStorage).filter(k => k.startsWith("sf_")).forEach(k => localStorage.removeItem(k)); } catch {}
  await sb.auth.signOut();
  return null;
}
